// src/routes/RouteErrorBoundary.tsx
import React from 'react'
import PageOverlay from '../components/pageoverlay/PageOverLay'
import { isChunkLoadError, reloadForChunkError } from './lazyWithRetry'

type RouteErrorBoundaryProps = {
  // Changing this key (e.g. the current pathname) clears a previous error so navigating away recovers.
  resetKey: string
  children: React.ReactNode
}

type RouteErrorBoundaryState = {
  hasError: boolean
  // A stale or missing chunk is recoverable: keep the loader up while the page reloads.
  isReloading: boolean
}

// shell (header/menus) stays usable instead of the whole app unmounting.
class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false, isReloading: false }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    if (isChunkLoadError(error)) {
      return { hasError: false, isReloading: true }
    }
    return { hasError: true, isReloading: false }
  }

  componentDidCatch(error: Error) {
    console.error('Failed to load page:', error)
    if (this.state.isReloading && !reloadForChunkError()) {
      // Already reloaded once for a chunk failure this session - stop and show the message.
      this.setState({ hasError: true, isReloading: false })
    }
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.isReloading) {
      return <PageOverlay />
    }
    if (this.state.hasError) {
      return (
        <div className='container'>
          <div className='d-flex align-items-center justify-content-center flex-column min-h-300 w-100'>
            <h2 className='font-16' tabIndex={0}>Sorry, this page could not be loaded.</h2>
            <button type='button' className='btn' onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default RouteErrorBoundary
